import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOrCreateUnnamedDraft } from "../proposals/proposalsApi";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { changeset, repoProposals } from "../lib/paths";
import { Spinner } from "../ui/Spinner";
import { useTranslation } from "../i18n";

// /studio/:slug/:repo → resolve the user's unnamed draft (get-or-create) and
// canonicalize to its numbered changeset URL. The bare repo URL is just an
// entry point; the editor always runs on a numbered changeset.
export function RepoHomeRedirect() {
  const { t } = useTranslation("app");
  const { slug = "", repo = "" } = useParams<{ slug: string; repo: string }>();
  // `?new=<path>` carries the file the repo-browser dialog just named. Without
  // it the typed path was dropped on the floor here and the editor landed on
  // whatever file sorted first — the user named a file and got someone else's.
  const [search] = useSearchParams();
  const wanted = search.get("new") ?? "";
  // The editor needs repo:write (get-or-create draft is a POST). A viewer (only
  // repo:read) would 403 and dead-end on every repo — send them to the read-only
  // changes list instead (bugloop L4 H2). Optimistic while the role loads.
  const wsQuery = useWorkspace(slug);
  const canEdit = wsQuery.data ? wsQuery.data.role !== "viewer" : null;

  const q = useQuery({
    queryKey: ["unnamed-draft", slug, repo],
    queryFn: () => getOrCreateUnnamedDraft(slug, repo),
    // Always re-resolve on mount. This is a get-or-create whose result becomes
    // invalid the moment the draft is named/proposed/accepted — caching it (the
    // 30s default) would redirect back to a just-accepted changeset instead of a
    // fresh Main draft after leaveToMain (bugloop L2 #2).
    staleTime: 0,
    gcTime: 0,
    enabled: canEdit === true,
  });

  // A failed workspace fetch (not a member, bad slug, 5xx) must show an error,
  // not spin forever — canEdit stays null on error and react-query doesn't retry
  // 4xx (bugloop L5 #1, regression from the H2 gating).
  if (wsQuery.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm font-medium text-red-300">{t("repo.openFailed")}</p>
        <p className="max-w-md font-mono text-xs text-slate-500">{String(wsQuery.error)}</p>
      </div>
    );
  }
  // Wait for the role, then bounce viewers to the read-only surface.
  if (canEdit === null || wsQuery.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (canEdit === false) {
    return <Navigate to={repoProposals(slug, repo)} replace />;
  }

  if (q.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm font-medium text-red-300">{t("repo.openFailed")}</p>
        <p className="max-w-md font-mono text-xs text-slate-500">{String(q.error)}</p>
      </div>
    );
  }
  // `?new=` stays a QUERY param through the redirect: the editor turns it into
  // a real (seeded) file exactly once and then strips it. As a path segment it
  // only opened a tab for a file nobody had created.
  return (
    <Navigate
      to={`${changeset(slug, repo, q.data.number)}${wanted ? `?new=${encodeURIComponent(wanted)}` : ""}`}
      replace
    />
  );
}
